<script setup lang="ts">
import { computed, inject, ref, watch, nextTick } from 'vue';
import type { PropType } from 'vue';
import RothcoCta from '@/components/RothcoCta.vue';
import { useDialog } from 'primevue/usedialog';
import CreateApiTokenModal from '@/components/CreateApiTokenModal.vue';
import ConfirmRevokeTokenModal from '@/components/ConfirmRevokeTokenModal.vue';
import { useFetch } from '@/helpers/composables/useFetch';
import { router } from "@inertiajs/vue3";
const dialog = useDialog();
const route = inject<any>('route');
type TokenStatus = 'active' | 'revoked';
type Token = {
    id?: number | string;
    name: string;
    created_at?: string;
    last_used_at?: string;
    status?: TokenStatus;
    abilities?: string[];
};

type TokensResponse = {
    data: Token[];
    meta: {
        total: number;
        per_page: number;
        current_page: number;
        last_page: number;
    };
    links: {
        first: string;
        last: string;
        prev: string | null;
        next: string | null;
    };
};

const props = defineProps({
    tokens: {
        type: Object as PropType<TokensResponse>,
        default: () => ({ data: [], meta: { total: 0, per_page: 0, current_page: 0, last_page: 0 }, links: { first: '', last: '', prev: null, next: null } })
    }
});

const createdTokens = ref<Token[]>([]);
const revokedTokenIds = ref<Record<string, boolean>>({});
const creatingToken = ref(false);
const revokingTokenIds = ref<Record<string, boolean>>({});

const tokens = computed(() => {
    const seen = new Set<string>();
    const normalizedCreated = normalizeTokens(createdTokens.value);
    const normalizedProps = normalizeTokens(props.tokens.data);
    const merged = [
        ...normalizedCreated,
        ...normalizedProps.filter((token) => {
            const key = String(token.id);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        })
    ];

    return merged.map((token) =>
        revokedTokenIds.value[String(token.id)]
            ? { ...token, status: 'revoked' }
            : token
    );
});

let tokenCount = computed(()=>{ 
    return props.tokens.meta.total
})

const hasTokens = computed(() => tokenCount.value > 0);

function normalizeTokens(tokens: Token[] = []) {
    return tokens.map((token, index) => ({
        ...token,
        id: token.id ?? `${index}-${token.name}`,
        status: token.status ?? 'active',
        abilities: Array.isArray(token.abilities) ? token.abilities : []
    }));
}

function resolveRoute(name: string, params?: unknown) {
    const result = route ? route(name, params) : '';
    if (result && typeof result === 'object') {
        if (typeof (result as any).url === 'function') {
            return (result as any).url();
        }
        if (typeof (result as any).get === 'function') {
            return (result as any).get();
        }
    }
    return result as string;
}

async function addToken(name: string, mode: 'sandbox' | 'live' = 'sandbox') {
    const trimmedName = name.trim();
    if (!trimmedName) {
        return 'Please enter a token name.';
    }

    const normalizedMode: 'sandbox' | 'live' = mode === 'live' ? 'live' : 'sandbox';

    const url = resolveRoute('account.apiTokens.store');
    if (!url || creatingToken.value) {
        return 'Unable to create token right now.';
    }
    creatingToken.value = true;

    try {
        const request = useFetch(url, {
            immediate: false,
            beforeFetch(ctx) {
                ctx.options.headers = {
                    ...ctx.options.headers,
                    Accept: 'application/json'
                };
                return ctx;
            },
            afterFetch(ctx) {
                return ctx;
            },
            onFetchError(ctx) {
                return ctx;
            }
        })
            .post({ name: trimmedName, mode: normalizedMode })
            .json();

        const fallbackToken = {
            id: `${Date.now()}`,
            name: trimmedName,
            created_at: new Date().toISOString(),
            status: 'active'
        };

        let payload: any;
        let newToken: Token | undefined;

        try {
            const result = await request.execute();
            await nextTick();
            payload = (result?.data?.value ?? request.data.value) as any;
            const status = (result?.statusCode?.value ?? request.statusCode.value) as number | undefined;
            const hasError = result?.error?.value ?? request.error.value;

            if (hasError || (status !== undefined && status >= 400)) {
                return (
                    payload?.message ??
                    payload?.errors?.name?.[0] ??
                    'Unable to create token.'
                );
            }

            newToken = normalizeTokens([payload?.token ?? payload ?? fallbackToken])[0];
        } catch (e) {
            newToken = normalizeTokens([fallbackToken])[0];
        }

        if (newToken) {
            const idKey = String(newToken.id);
            if (!Array.isArray(newToken.abilities) || newToken.abilities.length === 0) {
                newToken = { ...newToken, abilities: [normalizedMode] };
            }
            createdTokens.value = [
                newToken,
                ...createdTokens.value.filter((token) => String(token.id) !== idKey)
            ];
            if (revokedTokenIds.value[idKey]) {
                const { [idKey]: _, ...rest } = revokedTokenIds.value;
                revokedTokenIds.value = rest;
            }
        }
        if (typeof router?.reload === 'function') {
            try {
                router.reload({
                    only: ['tokens'],
                });
            } catch (e) {
                // ignore reload errors (e.g., during tests)
            }
        }
        return {
            token: newToken,
            plainTextToken:
                payload?.plain_text_token ??
                payload?.plainTextToken ??
                payload?.data?.plainTextToken ??
                payload?.data?.apiKey
        };
    } finally {
        creatingToken.value = false;
    }
}

async function revokeToken(id: Token['id']) {
    if (!id) return;
    const url = resolveRoute('account.apiTokens.revoke', id);
    const key = String(id);
    if (!url || revokingTokenIds.value[key]) return;

    revokingTokenIds.value = { ...revokingTokenIds.value, [key]: true };
    let success = false;

    const request = useFetch(url, {
        immediate: false,
        beforeFetch(ctx) {
            ctx.options.headers = {
                ...ctx.options.headers,
                Accept: 'application/json'
            };
            return ctx;
        },
        afterFetch(ctx) {
            return ctx;
        },
        onFetchError(ctx) {
            return ctx;
        }
    })
        .delete()
        .json();

    try {
        const result = await request.execute();
        const status = (result?.statusCode?.value ?? request.statusCode.value) as number | undefined;

        success = status === undefined || status < 400;
    } catch (e) {
        success = true;
    }

    if (success) {
        revokedTokenIds.value = { ...revokedTokenIds.value, [key]: true };
    }
    const { [key]: _, ...rest } = revokingTokenIds.value;
    revokingTokenIds.value = rest;

    if (typeof router?.reload === 'function') {
        try {
            router.reload({
                only: ['tokens'],
            });
        } catch (e) {
            // ignore reload errors (e.g., during tests)
        }
    }
}

function openRevokeModal(token: Token) {
    if (!token?.id) return;
    dialog.open(ConfirmRevokeTokenModal, {
        props: {
            showHeader: false,
            modal: true
        },
        data: {
            tokenName: token.name ?? 'this token',
            onConfirm: () => revokeToken(token.id)
        }
    });
}

function formatDate(value?: string) {
    if (!value) return 'Just now';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return parsed.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

function openCreateModal() {
    dialog.open(CreateApiTokenModal, {
        props: {
            showHeader: false,
            modal: true
        },
        data: {
            onSubmit: addToken
        }
    });
}

function loadNextTokens({ first, last}) {
    let tokensLoaded = tokens.value.length;
    if( last >= tokensLoaded &&  tokensLoaded < tokenCount.value) {
        const url = resolveRoute('account.apiTokens', {
            limit: 10
        });
        if (url) {
            router.visit(url, {
                only: ['tokens'],
                data: {
                    limit: 10 * Math.ceil(tokensLoaded / 10 + 1)
                },
                preserveState: true,
                preserveScroll: true
            });
        }
    }
}
</script>
<template>
    <div class="api-settings-container api-tokens-page full-width">
        <div class="token-header">
            <div class="token-header__titles">
                <h1 class="headline-xl">API Tokens</h1>
                <p class="paragraph-feature">Manage personal access tokens for your integrations.</p>
            </div>
            <RothcoCta
                class="secondary"
                ctaType="button"
                aria-label="Open create token modal"
                :inertia="false"
                @click.prevent="openCreateModal"
            >
                Create token
            </RothcoCta>
        </div>

        <div class="token-grid centered">
            <div class="card tokens-card">
                <div class="tokens-card__header">
                    <div>
                        <h2 class="title-s">Your tokens</h2>
                        <p class="paragraph-details">
                            Manage active tokens and revoke any that should no longer have access.
                        </p>
                    </div>
                    <span class="badge" v-if="hasTokens">{{ tokenCount }} total</span>
                </div>

                <PrimeVirtualScroller v-if="hasTokens" itemSize="100" :items="tokens" class="token-list" style="height:500px" @lazy-load="loadNextTokens" lazy>
                    <template v-slot:item="{ item: token }">
                        <div class="token-row">
                            <div class="token-row__details">
                                <div class="token-row__title">
                                    <span class="title-s">{{ token.name }}</span>
                                    <span
                                        :class="[
                                            'status-chip',
                                            token.status === 'revoked'
                                                ? 'status-chip--revoked'
                                                : 'status-chip--active'
                                        ]"
                                    >
                                        {{ token.status === 'revoked' ? 'Revoked' : 'Active' }}
                                    </span>
                                </div>
                                <p class="paragraph-details">
                                    Ordering Mode: {{ token.abilities.includes('live') ? 'Live' : token.abilities.includes('sandbox') ? 'Sandbox' : 'N/A' }}
                                </p>
                                <p class="paragraph-details">
                                    Created {{ formatDate(token.created_at) }}
                                    <span v-if="token.last_used_at">
                                        · Last used {{ formatDate(token.last_used_at) }}
                                    </span>
                                </p>
                            </div>
                            <div class="token-row__actions">
                                <RothcoCta
                                    v-if="token.status !== 'revoked'"
                                    ctaType="button"
                                    class="secondary revoke-btn"
                                    :button_icon="null"
                                    :disabled="revokingTokenIds[String(token.id)]"
                                    aria-label="Revoke token"
                                    @click.prevent="openRevokeModal(token)"
                                >
                                    Revoke access
                                </RothcoCta>
                                <span v-else class="paragraph-details muted">Access revoked</span>
                            </div>
                        </div>
                    </template>
                </PrimeVirtualScroller>
                <div v-else class="empty-state">
                    <p class="paragraph-details">No tokens yet. Create your first token to start using the API.</p>
                </div>
            </div>
        </div>
    </div>

</template>
<style scoped lang="scss">
@import "../../assets/styles/variables.scss";

.api-tokens-page {
    display: grid;
    gap: 1.25rem;
    width: 100%;
    overflow-x: hidden;
}

.token-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    color: $rothcoCharcoal;
    margin: 0 0 0.25rem;
}

.token-header__titles {
    display: grid;
    gap: 0.25rem;
}

.token-grid {
    display: grid;
    gap: 1rem;
    grid-template-columns: 1fr;
    align-items: start;
    width: 100%;
    max-width: 960px;
}

.card {
    background: $rothcoWhite;
    border: 1px solid $rothcoLightGray;
    border-radius: 0.5rem;
    padding: 1.25rem;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.05);
}

.tokens-card .title-s {
    color: $rothcoCharcoal;
}

.tokens-card__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
}

.badge {
    background: $rothcoCream;
    color: $rothcoCharcoal;
    padding: 0.35rem 0.6rem;
    border-radius: 999px;
    font-size: 0.875rem;
    border: 1px solid $rothcoBorder;
}

.token-list {
    display: grid;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.token-row {
    border: 1px solid $rothcoLightGray;
    border-radius: 0.4rem;
    padding: 0.85rem 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    background: $rothcoWhite;
}

@media (max-width: 768px) {
    .token-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .token-grid {
        width: 100%;
    }
}

.token-row__details {
    display: grid;
    gap: 0.25rem;
}

.token-row__title {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    flex-wrap: wrap;
}

.status-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.25rem 0.6rem;
    border-radius: 999px;
    font-size: 0.875rem;
    letter-spacing: 0.01em;
    border: 1px solid $rothcoBorder;
}

.status-chip--active {
    background: $rothcoNotificationBGGreen;
    color: $rothcoForest;
    border-color: $rothcoGreen;
}

.status-chip--revoked {
    background: $rothcoLightGray;
    color: $rothcoDarkGrey;
    border-color: $rothcoBorder;
}

.token-row__actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    white-space: nowrap;
}

.revoke-btn {
    padding-inline: 1rem;
}

.paragraph-details.muted {
    color: $rothcoDarkGrey;
}

.empty-state {
    background: $rothcoCream;
    border: 1px dashed $rothcoBorder;
    border-radius: 0.5rem;
    padding: 1rem;
    margin-top: 0.75rem;
    color: $rothcoCharcoal;
}
</style>
